// comments in English (code LTR, clean)
import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import HelmetInfo from "Components/HelmetInfo/HelmetInfo";
import BreadCrumb from "../../Components/BreadCrumb/BreadCrumb";
import cloud from "../../assets/cloud-upload.svg";
import { useTranslation } from "react-i18next";
import ContactUsAPI from "../../api/contentApi";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";

// comment: simple challenge generator (+ or -)
function genChallenge() {
  let a = Math.floor(Math.random() * 9) + 1; // 1..9
  let b = Math.floor(Math.random() * 9) + 1; // 1..9
  const op = Math.random() < 0.5 ? "+" : "-";
  if (op === "-" && b > a) [a, b] = [b, a]; // keep non-negative
  return { a, b, op };
}

const IndividaulsForm = () => {
  const { id } = useParams();
  const { t } = useTranslation("individualForm"); // Translation namespace
  const [isLoading, setLoading] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [fileError, setFileError] = useState("");

  // comment: recaptcha-like state
  const [challenge, setChallenge] = useState(genChallenge());
  const expected = useMemo(
    () => (challenge.op === "+" ? challenge.a + challenge.b : challenge.a - challenge.b),
    [challenge]
  );

// comments in English (code LTR, clean)
// comments in English (clean code)
const validationSchema = Yup.object({
  job_title: Yup.string()
    .matches(/^[A-Za-z\u0600-\u06FF\s]+$/, t("validation.lettersOnly")) // ✅ use translation
    .required(t("validation.jobTitle")),
  contact_time: Yup.string().required(t("validation.contactPeriod")),
  experience_years: Yup.string().required(t("validation.experience")),
  email: Yup.string()
    .email(t("validation.emailInvalid"))
    .required(t("validation.email")),
  name: Yup.string()
    .matches(/^[A-Za-z\u0600-\u06FF\s]+$/, t("validation.lettersOnly")) // ✅ use translation
    .required(t("validation.name")),
  humanAnswer: Yup.string()
    .required(t("validation.recaptcha"))
    .test("math-match", t("validation.recaptchaWrong"), (val) => {
      const num = Number(String(val || "").trim());
      return !Number.isNaN(num) && num === expected;
    }),
});



  const handleFileUpload = async (event, setFieldValue) => {
    setLoading(true);
    const file = event.target.files[0];
    if (!file) return;

    const maxFileSize = 5 * 1024 * 1024;
    if (file.size > maxFileSize) {
    setFileError(t("file_upload.max_size"));
      setLoading(false);
      return;
    }
    setFileError("");

    const formData = new FormData();
    formData.append("attachment[]", file);

    try {
      const response = await axios.post(
        "https://backend.ingaz.com.sa/api/admin/attachments",
        formData,
        { headers: { Accept: "application/json" } }
      );
      const uploadedFile = response.data.data[0];
      setFieldValue("resume", {
        thumbnail: uploadedFile.thumbnail,
        original: uploadedFile.original,
        id: uploadedFile.id,
      });
    toast.success(t("file_upload.success"));
    } catch (error) {
      console.error("Upload error:", error);
    toast.error(t("file_upload.failed"));
    } finally {
      setLoading(false);
    }
  };

  // comment: strip humanAnswer from payload; keep same API
  const handleSubmit = async (values, { resetForm, setFieldValue }) => {
    try {
      const { humanAnswer, ...rest } = values; // do not send humanAnswer
      const dataToSend = {
        name: rest.name,
        job_title: rest.job_title,
        experience_years: String(rest.experience_years),
        contact_time: rest.contact_time,
        email: rest.email,
        saudi: rest.saudi,
        our_service_id: 1,
        resume: {
          thumbnail: rest.resume.thumbnail,
          original: rest.resume.original,
          id: rest.resume.id || null,
        },
      };

      await ContactUsAPI.sendJobregister(dataToSend);
    toast.success(t("message_sent_success"), { autoClose: 5000 });

      resetForm();
      setFieldValue("resume", { thumbnail: "", original: "", id: null });
      setFileInputKey(Date.now());
      setChallenge(genChallenge()); // new challenge next time
    } catch (error) {
      toast.error("حدث خطأ أثناء الإرسال. حاول مرة أخرى.", { autoClose: 5000 });
      console.error(error);
    }
  };

  return (
    <>
      <ToastContainer />
      <HelmetInfo titlePage={"شروط الاستخدام"} />
      <div className="main-container flex xl:w-[1152px] flex-col gap-[55px] my-[164px] items-center flex-nowrap relative mx-auto px-5 xl:px-0">
        <div className="flex xl:w-[662px] flex-col gap-[64px] items-center shrink-0 flex-nowrap relative">
          <BreadCrumb name={t("breadcrumb.name")} />
          <div className="flex flex-col gap-[64px] items-center self-stretch shrink-0 flex-nowrap relative z-[6]">
            <div className="flex flex-col gap-[32px] items-center self-stretch shrink-0 flex-nowrap relative z-[7]">
              <span className="flex  xl:h-[58px] items-center shrink-0 basis-auto text-[32px] font-semibold leading-[58px] text-[#0d6b70] relative text-center z-[7]">
                {t("form.title")}
              </span>
              <span className="xl:h-[32px] self-stretch shrink-0 basis-auto text-[16px] font-normal leading-[32px] text-[#667680] relative text-center z-[8]">
                {t("form.subtitle")}
              </span>
            </div>
          </div>
        </div>

        <Formik
          initialValues={{
            name: "",
            job_title: "",
            experience_years: "",
            contact_time: "",
            email: "",
            saudi: "1",
            resume: { thumbnail: "", original: "", id: null },
            humanAnswer: "", // ✅ new field
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ setFieldValue, values }) => (
            <Form className="flex flex-col gap-[64px] items-start self-stretch shrink-0 flex-nowrap relative z-[9]">
              <div className="main-container flex w-full  xl:w-[564px] flex-col gap-[40px] items-start flex-nowrap relative mx-auto my-0">
                {/* Saudi/Non-Saudi Toggle */}
                <div className="flex pt-[6px] pr-[6px] pb-[6px] pl-[6px] items-center self-stretch shrink-0 flex-nowrap bg-[#f1f8fd] rounded-[99px] relative">
                  <button
                    type="button"
                    className={`flex pt-[8px] pr-[16px] pb-[8px] pl-[16px] gap-[8px] justify-center items-center grow shrink-0 basis-0 flex-nowrap ${
                      values.saudi === "1" ? "bg-[#0d6b70] text-[#fff]" : "bg-[#f1f8fd] text-[#667680]"
                    } rounded-[99px] border-none relative z-[3] pointer`}
                    onClick={() => setFieldValue("saudi", "1")}
                  >
                    {t("citizenship.saudi")}
                  </button>
                  <button
                    type="button"
                    className={`flex pt-[8px] pr-[16px] pb-[8px] pl-[16px] gap-[8px] justify-center items-center grow shrink-0 basis-0 flex-nowrap ${
                      values.saudi === "0" ? "bg-[#0d6b70] text-[#fff]" : "bg-[#f1f8fd] text-[#667680]"
                    } rounded-[99px] border-none relative z-[3] pointer`}
                    onClick={() => setFieldValue("saudi", "0")}
                  >
                    {t("citizenship.nonSaudi")}
                  </button>
                </div>

                {/* Job Title + Name */}
                <div className="flex w-full   flex-col md:flex-row xl:w-[564px]  gap-[8px]">
                  <div className="flex w-full  xl:w-[270px] flex-col gap-[8px]">
                    <label className="text-[#667680] text-[14px] font-medium">{t("fields.name")}</label>
                    <Field
                      name="name"
                      placeholder={t("fields.namePlaceholder")}
                      className="flex h-[56px] justify-center items-center self-stretch shrink-0 flex-nowrap bg-[#f4f4f4] rounded-[999px] border-none px-[16px] text-right"
                    />
                    <ErrorMessage name="name" component="div" className="text-red-500 text-[12px]" />
                  </div>

                  <div className="flex w-full  xl:w-[270px] flex-col gap-[8px]">
                    <label className="text-[#667680] text-[14px] font-medium">{t("fields.jobTitle")}</label>
                    <Field
                      name="job_title"
                      placeholder={t("fields.jobTitlePlaceholder")}
                      className="flex h-[56px] justify-center items-center self-stretch shrink-0 flex-nowrap bg-[#f4f4f4] rounded-[999px] border-none px-[16px] text-right"
                    />
                    <ErrorMessage name="job_title" component="div" className="text-red-500 text-[12px]" />
                  </div>
                </div>

                {/* Experience + Contact Period */}
                <div className="flex w-full flex-col md:flex-row  xl:w-[564px]  gap-[8px]">
                  <div className="flex w-full  xl:w-[270px] flex-col gap-[8px]">
                    <label className="text-[#667680] text-[14px] font-medium">{t("fields.experience")}</label>
                    <Field
                      type="number"
                      name="experience_years"
                      placeholder={t("fields.experience")}
                      className="flex h-[56px] justify-center items-center self-stretch shrink-0 flex-nowrap bg-[#f4f4f4] rounded-[999px] border-none px-[16px] text-right"
                    />
                    <ErrorMessage name="experience_years" component="div" className="text-red-500 text-[12px]" />
                  </div>

                  <div className="flex w-full  xl:w-[270px] flex-col gap-[8px]">
                    <label className="text-[#667680] text-[14px] font-medium">{t("fields.contactPeriod")}</label>
                    <Field
                      as="select"
                      name="contact_time"
                      className="flex h-[56px] justify-center items-center self-stretch shrink-0 flex-nowrap bg-[#f4f4f4] rounded-[999px] border-none px-[16px] rtl:text-right"
                    >
                      <option value="" disabled selected>
                        {t("fields.contactPeriod")}
                      </option>
                      <option value="morning">{t("morning")}</option>
                      <option value="evening">{t("Evening")}</option>
                    </Field>
                    <ErrorMessage name="contact_time" component="div" className="text-red-500 text-[12px]" />
                  </div>
                </div>

                {/* Email */}
                <div className="flex w-full  xl:w-[564px] flex-col gap-[8px]">
                  <label className="text-[#667680] text-[14px] font-medium">{t("fields.email")}</label>
                  <Field
                    name="email"
                    placeholder={t("fields.emailPlaceholder")}
                    className="flex h-[56px] justify-center items-center self-stretch shrink-0 flex-nowrap bg-[#f4f4f4] rounded-[999px] border-none px-[16px] text-right"
                  />
                  <ErrorMessage name="email" component="div" className="text-red-500 text-[12px]" />
                </div>

                {/* ✅ Recaptcha-like field (same design family) */}
                <div className="flex w-full  xl:w-[564px] flex-col gap-[8px]">
                  <label className="text-[#667680] text-[14px] font-medium">
                    {t("fields.recaptcha")} ({challenge.a} {challenge.op} {challenge.b} = ?)
                  </label>
                  <Field
                    name="humanAnswer"
                    inputMode="numeric"
                    placeholder={t("placeholders.recaptcha")}
                    className="flex h-[56px] justify-center items-center self-stretch shrink-0 flex-nowrap bg-[#f4f4f4] rounded-[999px] border-none px-[16px] text-right"
                  />
                  <ErrorMessage name="humanAnswer" component="div" className="text-red-500 text-[12px]" />
                </div>

                {/* CV Upload */}
                <div className="flex flex-col gap-[8px] w-full flex-nowrap relative mx-auto my-0">
                  <div className="flex xl:w-[135px] gap-[2px] items-center shrink-0 flex-nowrap relative">
                    <span className="h-[20px] shrink-0 basis-auto  text-[14px] font-medium leading-[20px] text-[#667680] relative text-left whitespace-nowrap z-[1]">
                      *
                    </span>
                    <span className="h-[20px] shrink-0 basis-auto  text-[14px] font-medium leading-[20px] text-[#667680] relative text-left whitespace-nowrap z-[2]">
                      {t("fields.cv")}
                    </span>
                  </div>
                  <div className="flex flex-col gap-[16px] items-start self-stretch shrink-0 flex-nowrap relative z-[3]">
                    <div className="flex pt-[16px] pr-[24px] pb-[16px] pl-[24px] flex-col gap-[4px] items-center self-stretch shrink-0 flex-nowrap rounded-[12px] border-solid border border-[#e7e7e7] relative z-[4]">
                      <div className="flex flex-col gap-[12px] items-center self-stretch shrink-0 flex-nowrap relative z-[5]">
                        <div className="w-[40px] h-[40px] shrink-0 rounded-[8px] border-solid border border-[#e7e7e7] flex justify-center items-center relative overflow-hidden z-[6]">
                          <div
                            className="w-[20px] h-[20px] bg-cover bg-no-repeat relative z-[7] "
                            style={{ backgroundImage: `url(${cloud})` }}
                          />
                        </div>
                        <div className="flex flex-col gap-[8px] items-center self-stretch shrink-0 flex-nowrap relative z-[8]">
                          <div className="flex gap-[4px] justify-center items-start self-stretch shrink-0 flex-nowrap relative z-[9]">
                            <div className="flex rtl:xl:w-[89px] gap-[6px] justify-center items-center shrink-0 flex-nowrap relative overflow-hidden z-[11]">
                              <label
                                htmlFor="cvUpload"
                                className="h-[20px] shrink-0 basis-auto  text-[14px] font-semibold leading-[20px] text-[#0d6b70] relative text-left whitespace-nowrap z-[12] pointer"
                              >
                                {t("fields.cvl.label")}
                              </label>
                              <input
                                key={fileInputKey}
                                id="cvUpload"
                                type="file"
                                className="hidden"
                                accept=".png, .jpg, .pdf"
                                onChange={(event) => handleFileUpload(event, setFieldValue)}
                              />
                            </div>
                            <span className="h-[20px] shrink-0 basis-auto  text-[14px] font-normal leading-[20px] text-[#667680] relative text-left xl:whitespace-nowrap z-10">
                              {t("fields.cvl.dragAndDrop")}
                            </span>
                          </div>
                          <span className="h-[18px] self-stretch shrink-0 basis-auto  text-[12px] font-normal leading-[18px] text-[#667680] relative text-center xl:whitespace-nowrap z-[13]">
                            {t("fields.cvl.note")}
                          </span>
                          <span className="h-[20px] shrink-0 basis-auto text-[14px] font-normal leading-[20px] text-[#667680] relative text-left xl:whitespace-nowrap z-10">
                          {isLoading ? t("uploading_cv") : t("upload_cv")}
                        </span>
                        </div>
                      </div>
                    </div>
                    {fileError && <div className="text-red-500 text-[12px]">{fileError}</div>}
                  </div>
                  <ErrorMessage name="cv" component="div" className="text-red-500 text-[12px]" />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="flex h-[56px] pt-[12px] pr-[49px] pb-[12px] pl-[49px] gap-[8px] justify-center items-center self-stretch shrink-0 flex-nowrap bg-[#0d6b70] rounded-[999px] border-none relative overflow-hidden z-[60] pointer"
                >
                  <span className="flex  xl:w-[43px] h-[29px] justify-center items-start shrink-0 basis-auto text-[16px] font-medium leading-[29px] text-[#fff] relative text-center whitespace-nowrap z-[61]">
                    {t("form.submit")}
                  </span>
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default IndividaulsForm;
